// External Imports
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';

import { BsFiletypeCsv, BsFiletypePdf } from 'react-icons/bs';
import { IoIosMail } from 'react-icons/io';

import Moment from 'moment';

//Components
import Mailer from './Mailer';
import ProcessingAlert from './ProcessingAlert';
import RequestingAlert from './alerts/RequestingAlert';

import { downloadVouchers, releaseVoucher } from '../utils/quota';

//Main View
export default function ListVouchers({
  title,
  customer_id,
  vouchers,
  updateVoucherList,
  bearerToken,
}) {
  // Local Data
  const [error, setError] = useState({ message: '', severity: 'primary' });

  const [processing, setProcessing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mailVisible, setMailVisible] = useState(false);

  // Event Handlers
  const handleRelease = async (customer_id, activation_code) => {
    const voucher = {
      activation_code: activation_code,
    };

    setError({ message: '', severity: 'success' });
    setDeleting(true);

    await releaseVoucher(bearerToken, customer_id, voucher)
      .then((data) => {
        setError({ message: 'Voucher released from customer!', severity: 'success' });
        setProcessing(true);
      })
      .catch((error) => {
        setError({ message: 'Unable to release voucher! ' + error, severity: 'danger' });
      });

    setDeleting(false);
  };

  // Event Handlers
  const vouchersUpdated = (tf) => {
    updateVoucherList();
    setProcessing(tf);
  };

  const downloadPdf = async () => {
    setRequesting(true);

    const codes = vouchers.map((voucher) => {
      return voucher.activation_code;
    });
    downloadVouchers(bearerToken, codes, 'pdf', customer_id)
      .then((url) => {
        //Open in new tab
        window.open(url, '_blank');

        setRequesting(false);
      })
      .catch((error) => {
        setError({ message: 'Unable to download PDF! ' + error, severity: 'danger' });
        setRequesting(false);
      });
  };

  const downloadCsv = async () => {
    setRequesting(true);

    const codes = vouchers.map((voucher) => {
      return voucher.activation_code;
    });
    downloadVouchers(bearerToken, codes, 'csv', customer_id)
      .then((url) => {
        // Download
        const a = document.createElement('a');
        a.href = url;
        a.download = 'vouchers.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setRequesting(false);
      })
      .catch((error) => {
        setError({ message: 'Unable to download CSV! ' + error, severity: 'danger' });
        setRequesting(false);
      });
  };

  const showMail = async () => {
    setMailVisible(true);
  };

  const hideMail = async (success = false) => {
    if (success) {
      setError({ message: 'Vouchers sent via e-mail!', severity: 'success' });
    }
    setMailVisible(false);
  };

  // Main View
  return (
    <>
      {error.message != '' && (
        <Alert variant={error.severity}>
          <span>{error.message}</span>
        </Alert>
      )}

      {processing && <ProcessingAlert changeFunction={vouchersUpdated} />}

      <Table striped>
        <thead>
          <tr>
            <th>Type</th>
            <th>Activation Code</th>
            <th>Status</th>
            <th>Expiry Date</th>
            <th>Quota</th>
            <th>Validity</th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {vouchers.length > 0 && (
            <>
              {vouchers.map((voucher) => (
                <tr
                  key={voucher.activation_code}
                  className={
                    voucher.status == 'generated'
                      ? 'table-success'
                      : voucher.status == 'expired'
                        ? 'table-danger'
                        : voucher.status == 'used'
                          ? 'table-dark'
                          : 'table-warning'
                  }
                >
                  <td>
                    {voucher.type == 'reset' && <span>Reset Token</span>}
                    {voucher.type == 'quota' && <span>Volume Voucher</span>}
                    {voucher.type == 'topup' && <span>Topup Token</span>}
                  </td>
                  <td>{voucher.activation_code}</td>
                  <td>
                    {voucher.status == 'generated' && <span>Available</span>}
                    {voucher.status == 'queued' && <span>Allocated to {voucher.customer_id}</span>}
                    {voucher.status == 'used' && <span>Used</span>}
                  </td>
                  <td>{Moment(voucher.expiry_date).format('YYYY-MM-DD')}</td>
                  <td>{voucher.quota_gb && <span>{voucher.quota_gb} GB</span>}</td>
                  <td>{voucher.validity_days && <span>{voucher.validity_days} days</span>}</td>
                  <td>
                    {voucher.status == 'queued' ? (
                      <>
                        {deleting ? (
                          <Alert variant='primary'>
                            <span>Calling API...</span>
                          </Alert>
                        ) : (
                          <Button
                            variant='danger'
                            onClick={() =>
                              handleRelease(voucher.customer_id, voucher.activation_code)
                            }
                          >
                            Release Voucher
                          </Button>
                        )}
                      </>
                    ) : (
                      <>&nbsp;</>
                    )}
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </Table>

      {vouchers.length > 0 && (
        <>
          {requesting ? (
            <RequestingAlert />
          ) : (
            <>
              <Button onClick={() => downloadPdf()} className='me-2'>
                <BsFiletypePdf size='1em' />
              </Button>
              <Button onClick={() => downloadCsv()} className='me-2'>
                <BsFiletypeCsv size='1em' />
              </Button>
              <Button onClick={() => showMail()}>
                <IoIosMail size='1em' />
              </Button>
            </>
          )}
        </>
      )}

      <Mailer
        visible={mailVisible}
        closeFunction={hideMail}
        subject={title}
        vouchers={vouchers}
        bearerToken={bearerToken}
        customer_id={customer_id}
      />

      {/* CUSTOMER#002: paging/scrolling/sorting */}
    </>
  );
}

ListVouchers.propTypes = {
  title: PropTypes.string,
  customer_id: PropTypes.string,
  vouchers: PropTypes.array,
  updateVoucherList: PropTypes.func,
  bearerToken: PropTypes.string,
};
